
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  section: 'Virtual Accounts',
  title: 'Add Virtual Accounts to your app',
  description: 'Use virtual accounts to gain additional insight and control over the flow of funds'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <p>{`In this guide, we'll walk through how to set up and manage virtual accounts. After integrating you will be able to send and receive money, make withdrawals and refunds, and reconcile funds for faster reconciliation.`}</p>
    <Callout mdxType="Callout">
      <p>{`This guide describes the latest integration pattern for virtual accounts, which uses a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` to represent the payout recipient. A `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` is required for new clients integrating with virtual accounts. A `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` remains optional for existing integrations. For more information about `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, see `}<a parentName="p" {...{
          "href": "/docs/api/users/"
        }}>{`User APIs`}</a>{`.`}</p>
    </Callout>
    <h4 {...{
      "id": "integrate-with-plaid-payment-initiation-and-enable-virtual-accounts"
    }}>{`Integrate with Plaid Payment Initiation and enable Virtual Accounts`}</h4>
    <p>{`Virtual accounts are designed to be used in conjunction with the Payment Initiation and Variable Recurring Payments APIs. To get the most out of Plaid virtual accounts, first `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/payment-initiation-one-time/add-to-app/"
      }}>{`add Payment Initiation to your app`}</a>{`.
In order to get started, you must first have a wallet set up. Virtual Accounts are not enabled by default in Sandbox. To get access in Sandbox or Production, `}<a parentName="p" {...{
        "href": "https://plaid.com/contact"
      }}>{`contact sales`}</a>{` or your account manager. Once enabled, create a virtual account via `}<inlineCode parentName="p">{`/wallet/create`}</inlineCode>{`.`}</p>
    <MDXCodeBlock type='server' title='/wallet/create' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewWalletCreateRequest(isoCurrencyCode)
response, _, err := client.PlaidApi.WalletCreate(ctx).WalletCreateRequest(*request).Execute()
walletID := response.GetWalletId()
balance := response.GetBalance()
numbers := response.GetNumbers()
recipientID := response.GetRecipientId()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.WalletCreateRequest{IsoCurrencyCode: isoCurrencyCode}
response, err := client.Wallet.Create(ctx, request)
walletID := response.GetWalletID()
balance := response.GetBalance()
numbers := response.GetNumbers()
recipientID := response.GetRecipientID()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`WalletCreateRequest request = new WalletCreateRequest()
  .isoCurrencyCode(isoCurrencyCode);
Response<WalletCreateResponse> response = client()
  .walletCreate(request)
  .execute();
String walletId = response.body().getWalletId();
WalletBalance balance = response.body().getBalance();
WalletNumbers numbers = response.body().getNumbers();
String recipientId = response.body().getRecipientId();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::WalletCreateRequest.new({ iso_currency_code: iso_currency_code })
response = client.wallet_create(request)
wallet_id = response.wallet_id
balance = response.balance
numbers = response.numbers
recipient_id = response.recipient_id
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: WalletCreateRequest = {
  iso_currency_code: isoCurrencyCode,
};
try {
  const response = await plaidClient.walletCreate(request);
  const walletID = response.data.wallet_id;
  const balance = response.data.balance;
  const numbers = response.data.numbers;
  const recipientID = response.data.recipient_id;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/wallet/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "iso_currency_code": "\${ISO_CURRENCY_CODE}"
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = WalletCreateRequest(iso_currency_code=WalletISOCurrencyCode(iso_currency_code))
response = client.wallet_create(request)
wallet_id = response['wallet_id']
balance = response['balance']
numbers = response['numbers']
recipient_id = response['recipient_id']
`}</code></pre>
    </MDXCodeBlock>
    <h4 {...{
      "id": "enable-virtual-account-transaction-webhooks"
    }}>{`Enable Virtual Account Transaction webhooks`}</h4>
    <p>{`Enable `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/virtual-accounts/managing-virtual-accounts/#webhooks"
      }}>{`virtual account webhooks`}</a>{` to receive notifications when new transaction events occur. Plaid will send a webhook for each transaction event. These notifications can be used to stay updated on transaction status information.`}</p>
    <h4 {...{
      "id": "funding-your-virtual-account-through-payment-initiation"
    }}>{`Funding your Virtual Account through Payment Initiation`}</h4>
    <h5 {...{
      "id": "create-a-payment-that-will-be-initiated-to-your-virtual-account"
    }}>{`Create a Payment that will be initiated to your virtual account`}</h5>
    <p>{`Pass a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` representing the sender of the payment. If you don't already have one, create it by calling `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{`. For details, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/payment-initiation-one-time/add-to-app/#create-a-user"
      }}>{`Create a user`}</a>{`.`}</p>
    <MDXCodeBlock type='server' title='/payment_initiation/payment/create' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewPaymentInitiationPaymentCreateRequest(
  recipientID,
  "TestPayment",
  *plaid.NewPaymentAmount("GBP", 100.0),
)
request.SetUserId(userID)

response, _, err := client.PlaidApi.PaymentInitiationPaymentCreate(ctx).PaymentInitiationPaymentCreateRequest(*request).Execute()
paymentID := response.GetPaymentId()
status := response.GetStatus()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.PaymentInitiationPaymentCreateRequest{
    RecipientID: recipientID,
    Reference:   "TestPayment",
    Amount: &plaid.PaymentAmount{
        Currency: "GBP",
        Value:    100.0,
    },
}
request.UserID = &userID

response, err := client.PaymentInitiation.CreatePayment(ctx, request)
paymentID := response.GetPaymentID()
status := response.GetStatus()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = PaymentInitiationPaymentCreateRequest(
    recipient_id=recipient_id,
    user_id=user_id,
    reference='TestPayment',
    amount=PaymentAmount(
        currency=PaymentAmountCurrency('GBP'),
        value=100.00
    )
)
response = client.payment_initiation_payment_create(request)
payment_id = response["payment_id"]
status = response["status"]
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`PaymentAmount amount = new PaymentAmount()
  .currency(PaymentAmountCurrency.GBP)
  .value(100.00);
PaymentInitiationPaymentCreateRequest request = new PaymentInitiationPaymentCreateRequest()
  .recipientId(recipientId)
  .userId(userId)
  .reference("TestPayment")
  .amount(amount);
Response<PaymentInitiationPaymentCreateResponse> response = client
  .paymentInitiationPaymentCreate(request)
  .execute();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/payment_initiation/payment/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "recipient_id": "\${RECIPIENT_ID}",
  "user_id": "\${USER_ID}",
  "reference": "TestPayment",
  "amount": {
    "value": 100.00,
    "currency": "GBP"
  }
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::PaymentInitiationPaymentCreateRequest.new(
  {
    recipient_id: recipient_id,
    user_id: user_id,
    reference: "TestPayment",
    amount: {
      value: 100.00,
      currency: "GBP"
    }
  }
)
response = client.payment_initiation_payment_create(request)
payment_id = response.payment_id
status = response.status
`}</code></pre>
      {/* prettier-ignore */}
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: PaymentInitiationPaymentCreateRequest = {
  recipient_id: recipientID,
  user_id: userID,
  reference: 'TestPayment',
  amount: {
    currency: 'GBP',
    value: 100.00,
  },
};
try {
  const response = await plaidClient.paymentInitiationPaymentCreate(request);
  const paymentID = response.data.payment_id;
  const status = response.data.status;
} catch (error) {
  // handle error
}
`}</code></pre>
    </MDXCodeBlock>
    <p>{`Use the `}<inlineCode parentName="p">{`recipient_id`}</inlineCode>{` associated with your `}<inlineCode parentName="p">{`wallet_id`}</inlineCode>{` so Plaid knows to route the payment to your virtual account.`}</p>
    <h5 {...{
      "id": "launch-and-complete-the-payment-initiation-flow-in-link"
    }}>{`Launch and complete the Payment Initiation flow in Link`}</h5>
    <p>{`For more details on how to do this, see the `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/payment-initiation-one-time/add-to-app/#launch-the-payment-initiation-flow-in-link"
      }}>{`Payment Initiation documentation`}</a>{`.`}</p>
    <h5 {...{
      "id": "confirm-that-the-payment-has-settled-via-a-payment-webhook"
    }}>{`Confirm that the payment has settled via a payment webhook`}</h5>
    <p>{`For more details, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/payment-initiation/#payment_status_update"
      }}><inlineCode parentName="a">{`PAYMENT_STATUS_UPDATE`}</inlineCode></a>{` webhook.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#PAYMENT_STATUS_UPDATE",
        "#PAYMENT_STATUS_UPDATE": true
      }}>{`{
  "webhook_type": "PAYMENT_INITIATION",
  "webhook_code": "PAYMENT_STATUS_UPDATE",
  "payment_id": "<PAYMENT_ID>",
  "new_payment_status": "PAYMENT_STATUS_SETTLED",
  "old_payment_status": "PAYMENT_STATUS_EXECUTED",
  "original_reference": "Account Funding 99744",
  "adjusted_reference": "Account Funding 99",
  "original_start_date": "2017-09-14",
  "adjusted_start_date": "2017-09-15",
  "timestamp": "2017-09-14T14:42:19.350Z",
  "environment": "production"
}
`}</code></pre>
    <h5 {...{
      "id": "confirm-that-funds-have-arrived-in-your-virtual-account-via-a-transaction-webhook"
    }}>{`Confirm that funds have arrived in your virtual account via a transaction webhook`}</h5>
    <p>{`For more details, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/virtual-accounts/#wallet_transaction_status_update"
      }}><inlineCode parentName="a">{`WALLET_TRANSACTION_STATUS_UPDATE`}</inlineCode></a>{` webhook.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#WALLET_TRANSACTION_STATUS_UPDATE",
        "#WALLET_TRANSACTION_STATUS_UPDATE": true
      }}>{`{
  "webhook_type": "WALLET",
  "webhook_code": "WALLET_TRANSACTION_STATUS_UPDATE",
  "transaction_id": "<TRANSACTION_ID>",
  "payment_id": "<PAYMENT_ID>",
  "wallet_id": "<WALLET_ID>",
  "new_status": "SETTLED",
  "old_status": "INITIATED",
  "timestamp": "2021-09-14T14:42:19.350Z",
  "environment": "production"
}
`}</code></pre>
    <h4 {...{
      "id": "executing-a-payout"
    }}>{`Executing a Payout`}</h4>
    <h5 {...{
      "id": "fetch-your-virtual-account-to-confirm-it-has-sufficient-balance"
    }}>{`Fetch your virtual account to confirm it has sufficient balance`}</h5>
    <MDXCodeBlock type='server' title='/wallet/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewWalletGetRequest(walletID)
response, _, err := client.PlaidApi.WalletGet(ctx).WalletGetRequest(*request).Execute()
balance := response.GetBalance()
numbers := response.GetNumbers()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.WalletGetRequest{WalletID: walletID}
response, err := client.Wallet.Get(ctx, request)
balance := response.GetBalance()
numbers := response.GetNumbers()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`WalletGetRequest request = new WalletGetRequest()
  .walletId(walletId);
Response<WalletGetResponse> response = client()
  .walletGet(request)
  .execute();
WalletBalance balance = response.body().getBalance();
WalletNumbers numbers = response.body().getNumbers();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::WalletGetRequest.new({ wallet_id: wallet_id })
response = client.wallet_get(request)
wallet_id = response.wallet_id
balance = response.balance
numbers = response.numbers
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: WalletGetRequest = {
  wallet_id: walletID,
};
try {
  const response = await plaidClient.walletGet(request);
  const walletID = response.data.wallet_id;
  const balance = response.data.balance;
  const numbers = response.data.numbers;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/wallet/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "wallet_id": "\${WALLET_ID}"
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = WalletGetRequest(wallet_id=wallet_id)
response = client.wallet_get(request)
wallet_id = response['wallet_id']
balance = response['balance']
numbers = response['numbers']
`}</code></pre>
    </MDXCodeBlock>
    <h5 {...{
      "id": "execute-a-payout"
    }}>{`Execute a payout`}</h5>
    <p>{`Payouts must be associated with a `}<inlineCode parentName="p">{`user_id`}</inlineCode>{` representing the recipient of the funds. If you don't already have one, create it by calling `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` with a unique `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` and an `}<inlineCode parentName="p">{`identity`}</inlineCode>{` object containing the recipient's `}<inlineCode parentName="p">{`name`}</inlineCode>{` plus at least one of `}<inlineCode parentName="p">{`emails`}</inlineCode>{` or `}<inlineCode parentName="p">{`phone_numbers`}</inlineCode>{`. Calling `}<inlineCode parentName="p">{`/user/create`}</inlineCode>{` again with the same `}<inlineCode parentName="p">{`client_user_id`}</inlineCode>{` returns the same `}<inlineCode parentName="p">{`user_id`}</inlineCode>{`, so you can re-use it for future payouts to the same recipient. For a worked example, see `}<a parentName="p" {...{
        "href": "/docs/payment-initiation/payment-initiation-one-time/add-to-app/#create-a-user"
      }}>{`Creating a user`}</a>{`; for the full request schema, see `}<a parentName="p" {...{
        "href": "/docs/api/users/"
      }}>{`User endpoints`}</a>{`.`}</p>
    <MDXCodeBlock type='server' title='/wallet/transaction/execute' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`counterpartyBACS := plaid.NewWalletTransactionCounterpartyBACS()
counterpartyBACS.SetAccount("12345678")
counterpartyBACS.SetSortCode("123456")

request := plaid.NewWalletTransactionExecuteRequest(
  "39fae5f2-b2b4-48b6-a363-5328995b2753",
  walletID,
  plaid.WalletTransactionCounterparty{
    Name: "Test",
    Numbers: plaid.WalletTransactionCounterpartyNumbers{
      Bacs: counterpartyBACS,
    },
  },
  plaid.WalletTransactionAmount{
    Value: 1.00,
    IsoCurrencyCode: plaid.WALLETISOCURRENCYCODE_GBP,
  },
  "transaction ABC123",
)
request.SetUserId(userID)
response, _, err := client.PlaidApi.WalletTransactionExecute(ctx).WalletTransactionExecuteRequest(*request).Execute()
transactionID := response.GetTransactionId()
status := response.GetStatus()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`counterpartyBACS := &plaid.WalletTransactionCounterpartyBacs{}
counterpartyBACS.Account = plaid.String("12345678")
counterpartyBACS.SortCode = plaid.String("123456")

request := &plaid.WalletTransactionExecuteRequest{
    IdempotencyKey: "39fae5f2-b2b4-48b6-a363-5328995b2753",
    WalletID:       walletID,
    Counterparty: &plaid.WalletTransactionCounterparty{
        Name:    "Test",
        Numbers: &plaid.WalletTransactionCounterpartyNumbers{Bacs: counterpartyBACS},
    },
    Amount: &plaid.WalletTransactionAmount{
        Value:           1.00,
        IsoCurrencyCode: plaid.WalletIsoCurrencyCodeGbp,
    },
    Reference: "transaction ABC123",
}
request.UserID = &userID
response, err := client.Wallet.ExecuteTransaction(ctx, request)
transactionID := response.GetTransactionID()
status := response.GetStatus()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`WalletTransactionCounterparty counterparty = new WalletTransactionCounterparty()
  .name("Test")
  .numbers(new WalletTransactionCounterpartyNumbers()
    .bacs(new WalletTransactionCounterpartyBACS()
      .account("12345678")
      .sortCode("123456")
    )
  );
WalletTransactionAmount amount = new WalletTransactionAmount()
  .value(1.00)
  .isoCurrencyCode(WalletISOCurrencyCode.GBP);

WalletTransactionExecuteRequest request = new WalletTransactionExecuteRequest()
  .walletId(walletId)
  .userId(userId)
  .counterparty(counterparty)
  .amount(amount)
  .reference("transaction ABC123")
  .idempotencyKey("39fae5f2-b2b4-48b6-a363-5328995b2753");

Response<WalletTransactionExecuteResponse> response = client()
  .walletTransactionExecute(request)
  .execute();
String transactionId = response.body().getTransactionId();
WalletTransactionStatus status = response.body().getStatus();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::WalletTransactionExecuteRequest.new(
  {
    wallet_id: wallet_id,
    user_id: user_id,
    counterparty: {
      name: 'Test',
      numbers: {
        bacs: {
          account: '12345678',
          sort_code: '123456'
        }
      }
    },
    amount: {
      value: 1,
      iso_currency_code: 'GBP'
    },
    reference: 'transaction ABC123',
    idempotency_key: '39fae5f2-b2b4-48b6-a363-5328995b2753'
  }
)
response = client.wallet_transaction_execute(request)
transaction_id = response.transaction_id
status = response.status
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: WalletTransactionExecuteRequest = {
  wallet_id: walletID,
  user_id: userID,
  counterparty: {
    name: 'Test',
    numbers: {
      bacs: {
        account: '12345678',
        sort_code: '123456',
      },
    },
  },
  amount: {
    value: 1,
    iso_currency_code: 'GBP',
  },
  reference: 'transaction ABC123',
  idempotency_key: '39fae5f2-b2b4-48b6-a363-5328995b2753',
};
try {
  const response = await plaidClient.walletTransactionExecute(request);
  const transactionID = response.data.transaction_id;
  const status = response.data.status;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/wallet/transaction/execute \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "wallet_id": "\${WALLET_ID}",
  "user_id": "\${USER_ID}",
  "counterparty": {
    "name": "Test",
    "numbers": {
      "bacs": {
        "account": "12345678",
        "sort_code": "123456"
      }
    }
  },
  "amount": {
    "value": 1,
    "iso_currency_code": "GBP"
  },
  "reference": "transaction ABC123",
  "idempotency_key": "39fae5f2-b2b4-48b6-a363-5328995b2753"
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = WalletTransactionExecuteRequest(
  wallet_id=wallet_id,
  user_id=user_id,
  counterparty=WalletTransactionCounterparty(
    name='Test',
    numbers=WalletTransactionCounterpartyNumbers(
      bacs=WalletTransactionCounterpartyBACS(
        account='12345678',
        sort_code='123456'
      )
    )
  ),
  amount=WalletTransactionAmount(
    value=1.0,
    iso_currency_code=WalletISOCurrencyCode('GBP')
  ),
  reference='transaction ABC123',
  idempotency_key=WalletTransactionIdempotencyKey('39fae5f2-b2b4-48b6-a363-5328995b2753')
)
response = client.wallet_transaction_execute(request)
transaction_id = response['transaction_id']
status = response['status']
`}</code></pre>
    </MDXCodeBlock>
    <h5 {...{
      "id": "confirm-the-payout-was-executed-via-a-transaction-webhook"
    }}>{`Confirm the payout was executed, via a transaction webhook`}</h5>
    <p>{`For more details, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/virtual-accounts/#wallet_transaction_status_update"
      }}><inlineCode parentName="a">{`WALLET_TRANSACTION_STATUS_UPDATE`}</inlineCode></a>{` webhook.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#WALLET_TRANSACTION_STATUS_UPDATE",
        "#WALLET_TRANSACTION_STATUS_UPDATE": true
      }}>{`{
  "webhook_type": "WALLET",
  "webhook_code": "WALLET_TRANSACTION_STATUS_UPDATE",
  "transaction_id": "<TRANSACTION_ID>",
  "wallet_id": "<WALLET_ID>",
  "new_status": "EXECUTED",
  "old_status": "INITIATED",
  "timestamp": "2021-09-14T14:42:19.350Z"
}
`}</code></pre>
    <h4 {...{
      "id": "refunding-a-payment-initiation-payment"
    }}>{`Refunding a Payment Initiation payment`}</h4>
    <h5 {...{
      "id": "fetch-your-virtual-account-to-confirm-it-has-sufficient-balance-1"
    }}>{`Fetch your virtual account to confirm it has sufficient balance`}</h5>
    <MDXCodeBlock type='server' title='/wallet/get' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewWalletGetRequest(walletID)
response, _, err := client.PlaidApi.WalletGet(ctx).WalletGetRequest(*request).Execute()
balance := response.GetBalance()
numbers := response.GetNumbers()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.WalletGetRequest{WalletID: walletID}
response, err := client.Wallet.Get(ctx, request)
balance := response.GetBalance()
numbers := response.GetNumbers()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`WalletGetRequest request = new WalletGetRequest()
  .walletId(walletId);
Response<WalletGetResponse> response = client()
  .walletGet(request)
  .execute();
WalletBalance balance = response.body().getBalance();
WalletNumbers numbers = response.body().getNumbers();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::WalletGetRequest.new({ wallet_id: wallet_id })
response = client.wallet_get(request)
wallet_id = response.wallet_id
balance = response.balance
numbers = response.numbers
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: WalletGetRequest = {
  wallet_id: walletID,
};
try {
  const response = await plaidClient.walletGet(request);
  const walletID = response.data.wallet_id;
  const balance = response.data.balance;
  const numbers = response.data.numbers;
} catch (error) {
  // handle error
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/wallet/get \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "wallet_id": "\${WALLET_ID}"
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = WalletGetRequest(wallet_id=wallet_id)
response = client.wallet_get(request)
wallet_id = response['wallet_id']
balance = response['balance']
numbers = response['numbers']
`}</code></pre>
    </MDXCodeBlock>
    <h5 {...{
      "id": "refund-a-payment-initiation-payment"
    }}>{`Refund a Payment Initiation payment`}</h5>
    <MDXCodeBlock type='server' title='/payment_initiation/payment/reverse' mdxType="MDXCodeBlock">
      <pre><code parentName="pre" {...{
          "className": "language-python"
        }}>{`request = PaymentInitiationPaymentReverseRequest(
  payment_id=payment_id,
  reference='RefundABC123',
  idempotency_key=WalletTransactionIdempotencyKey('ae009325-df8d-4f52-b1e0-53ff26c23912')
)
response = client.payment_initiation_payment_reverse(request)
refund_id = response['refund_id']
status = response['status']
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go"
        }}>{`request := plaid.NewPaymentInitiationPaymentReverseRequest(
  paymentID,
  "ae009325-df8d-4f52-b1e0-53ff26c23912",
  "RefundABC123",
)
response, _, err := client.PlaidApi.PaymentInitiationPaymentReverse(ctx).PaymentInitiationPaymentReverseRequest(*request).Execute()
refundID := response.GetRefundId()
status := response.GetStatus()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-go",
          "metastring": "sdk=fern",
          "sdk": "fern"
        }}>{`request := &plaid.PaymentInitiationPaymentReverseRequest{
    PaymentID:      paymentID,
    IdempotencyKey: "ae009325-df8d-4f52-b1e0-53ff26c23912",
    Reference:      "RefundABC123",
}
response, err := client.PaymentInitiation.ReversePayment(ctx, request)
refundID := response.GetRefundID()
status := response.GetStatus()
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-bash"
        }}>{`curl -X POST https://sandbox.plaid.com/payment_initiation/payment/reverse \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "payment_id": "\${PAYMENT_ID}",
  "reference": "RefundABC123",
  "idempotency_key": "ae009325-df8d-4f52-b1e0-53ff26c23912"
}'
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-ruby"
        }}>{`request = Plaid::PaymentInitiationPaymentReverseRequest.new(
  {
    payment_id: payment_id,
    reference: 'RefundABC123',
    idempotency_key: 'ae009325-df8d-4f52-b1e0-53ff26c23912'
  }
)
response = client.payment_initiation_payment_reverse(request)
refund_id = response.refund_id
status = response.status
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-java"
        }}>{`PaymentInitiationPaymentReverseRequest request = new PaymentInitiationPaymentReverseRequest()
  .paymentId(paymentId)
  .reference("RefundABC123")
  .idempotencyKey("ae009325-df8d-4f52-b1e0-53ff26c23912");

Response<PaymentInitiationPaymentReverseResponse> response = client()
  .paymentInitiationPaymentReverse(request)
  .execute();
String refundId = response.body().getRefundId();
WalletTransactionStatus status = response.body().getStatus();
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-node"
        }}>{`const request: PaymentInitiationPaymentReverseRequest = {
  payment_id: paymentID,
  reference: 'RefundABC123',
  idempotency_key: 'ae009325-df8d-4f52-b1e0-53ff26c23912',
};
try {
  const response = await plaidClient.paymentInitiationPaymentReverse(request);
  const refundID = response.data.refund_id;
  const status = response.data.status;
} catch (error) {
  // handle error
}
`}</code></pre>
    </MDXCodeBlock>
    <h5 {...{
      "id": "confirm-the-refund-was-executed-via-a-transaction-webhook"
    }}>{`Confirm the refund was executed, via a transaction webhook`}</h5>
    <p>{`For more details, see the `}<a parentName="p" {...{
        "href": "/docs/api/products/virtual-accounts/#wallet_transaction_status_update"
      }}><inlineCode parentName="a">{`WALLET_TRANSACTION_STATUS_UPDATE`}</inlineCode></a>{` webhook.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "#WALLET_TRANSACTION_STATUS_UPDATE",
        "#WALLET_TRANSACTION_STATUS_UPDATE": true
      }}>{`{
  "webhook_type": "WALLET",
  "webhook_code": "WALLET_TRANSACTION_STATUS_UPDATE",
  "transaction_id": "<TRANSACTION_ID>",
  "wallet_id": "<WALLET_ID>",
  "new_status": "EXECUTED",
  "old_status": "INITIATED",
  "timestamp": "2021-09-14T14:42:19.350Z"
}
`}</code></pre>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "integrate-with-plaid-payment-initiation-and-enable-virtual-accounts",
        level: 4,
        title: "Integrate with Plaid Payment Initiation and enable Virtual Accounts",
      },
{
        id: "enable-virtual-account-transaction-webhooks",
        level: 4,
        title: "Enable Virtual Account Transaction webhooks",
      },
{
        id: "funding-your-virtual-account-through-payment-initiation",
        level: 4,
        title: "Funding your Virtual Account through Payment Initiation",
      },
{
        id: "executing-a-payout",
        level: 4,
        title: "Executing a Payout",
      },
{
        id: "refunding-a-payment-initiation-payment",
        level: 4,
        title: "Refunding a Payment Initiation payment",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
